import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  booleanAttribute,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { NgIf } from '@angular/common';
import { DsIconButtonV2Component } from '../buttons/ds-icon-button-v2.component';
import { DsIconComponent } from '../icons/ds-icon.component';
import type { DsIconName } from '../icons/ds-icons';
import { DsScrollComponent } from '../styles/ds-scroll';
import {
  DsGlobalOverlayAttachment,
  DsGlobalOverlayHostService,
} from './ds-global-overlay-host.service';

@Component({
  selector: 'ds-modal-bottom-sheet',
  standalone: true,
  imports: [NgIf, DsIconButtonV2Component, DsIconComponent, DsScrollComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="ds-modal-bottom-sheet"
      [class.ds-modal-bottom-sheet--visible]="visible"
      [class.ds-modal-bottom-sheet--entering]="_entering"
      [style.--ds-modal-bottom-sheet-viewport-height.px]="_viewportHeight"
      [style.--ds-modal-bottom-sheet-keyboard-offset.px]="_keyboardOffset"
      role="dialog"
      aria-modal="true"
      [attr.aria-label]="title || 'Modal bottom sheet'"
      (click)="onOverlayClick($event)"
    >
      <div
        class="ds-modal-bottom-sheet__sheet"
        [style.--ds-modal-bottom-sheet-resolved-max-width]="resolvedMaxWidth"
        (click)="$event.stopPropagation()"
      >
        <div class="ds-modal-bottom-sheet__header">
          <div
            class="ds-modal-bottom-sheet__header-center"
            [class.ds-modal-bottom-sheet__header-center--has-close]="showCloseButton"
          >
            <div class="ds-modal-bottom-sheet__title-actions">
              <ng-content select="[ds-modal-bottom-sheet-header-left]" />
            </div>
            <div *ngIf="showIcon || title" class="ds-modal-bottom-sheet__title-icon">
              <div *ngIf="showIcon" class="ds-modal-bottom-sheet__icon" [style.color]="iconColor">
                <ds-icon [name]="iconName" [size]="24" />
              </div>
              <h2 *ngIf="title" class="ds-modal-bottom-sheet__title ds-text-h2-bold">{{ title }}</h2>
            </div>
          </div>
          <div class="ds-modal-bottom-sheet__header-column ds-modal-bottom-sheet__header-column--right">
            <ng-content select="[ds-modal-bottom-sheet-header-right]" />
            <div *ngIf="showCloseButton" class="ds-modal-bottom-sheet__close-container">
              <ds-icon-button-v2
                icon="close"
                size="s"
                emphasis="secondary-filled"
                ariaLabel="Cerrar"
                (clicked)="dismissed.emit()"
              />
            </div>
          </div>
        </div>

        <div dsScroll class="ds-modal-bottom-sheet__body">
          <div *ngIf="description" class="ds-modal-bottom-sheet__text">
            <p *ngIf="description" class="ds-modal-bottom-sheet__description ds-text-body-regular">{{ description }}</p>
          </div>

          <ng-content select=":not([ds-modal-bottom-sheet-action])" />
        </div>

        <div class="ds-modal-bottom-sheet__footer">
          <ng-content select="[ds-modal-bottom-sheet-action]" />
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host { display: contents; }

    .ds-modal-bottom-sheet {
      position:         fixed;
      inset:            0;
      z-index:          var(--ds-modal-bottom-sheet-z-index);
      display:          flex;
      align-items:      flex-end;
      justify-content:  center;
      pointer-events:   none;
      box-sizing:       border-box;
      padding-bottom:   var(--ds-modal-bottom-sheet-keyboard-offset, 0px);
      opacity:          0;
      transition:       opacity var(--ds-modal-bottom-sheet-transition-duration) ease;
    }

    .ds-modal-bottom-sheet--visible {
      pointer-events: auto;
      opacity: 1;
    }

    /* Pseudo-element carries the blurred backdrop so it doesn't blur the sheet */
    .ds-modal-bottom-sheet::before {
      content:           '';
      position:          fixed;
      inset:             0;
      background:        var(--ds-modal-bottom-sheet-overlay);
      backdrop-filter:   blur(0);
      -webkit-backdrop-filter: blur(0);
      opacity:           0;
      transition:
        opacity var(--ds-modal-bottom-sheet-backdrop-transition-duration) ease,
        backdrop-filter var(--ds-modal-bottom-sheet-backdrop-transition-duration) cubic-bezier(0.22, 1, 0.36, 1),
        -webkit-backdrop-filter var(--ds-modal-bottom-sheet-backdrop-transition-duration) cubic-bezier(0.22, 1, 0.36, 1);
      pointer-events:    none;
    }

    .ds-modal-bottom-sheet--visible::before {
      opacity: 1;
      backdrop-filter: blur(var(--ds-modal-bottom-sheet-backdrop-blur));
      -webkit-backdrop-filter: blur(var(--ds-modal-bottom-sheet-backdrop-blur));
    }

    /* ── Sheet ───────────────────────────────────────────────── */
    .ds-modal-bottom-sheet__sheet {
      position:         relative;
      z-index:          1;
      width:            100%;
      box-sizing:       border-box;
      background:       var(--ds-modal-bottom-sheet-background);
      border-radius:    var(--ds-modal-bottom-sheet-border-radius) var(--ds-modal-bottom-sheet-border-radius) 0 0;
      overflow:         hidden;
      transform:        translateY(100%);
      transition:       transform var(--ds-modal-bottom-sheet-transition-duration) cubic-bezier(0.22, 1, 0.36, 1);
      display:          flex;
      flex-direction:   column;
      max-height:       calc(var(--ds-modal-bottom-sheet-viewport-height, 100dvh) - var(--ds-modal-bottom-sheet-max-height-offset, 48px));
    }

    .ds-modal-bottom-sheet--visible .ds-modal-bottom-sheet__sheet {
      transform: translateY(0);
    }

    /* ── Header (close button row) ───────────────────────────── */
    .ds-modal-bottom-sheet__header {
      position:         relative;
      z-index:          2;
      display:          grid;
      grid-template-columns:
        minmax(0, 1fr)
        max-content;
      align-items:      flex-start;
      gap:              var(--ds-sheet-header-gap, 8px);
      padding:          var(--ds-sheet-header-padding-y) var(--ds-sheet-header-padding-x) var(--ds-sheet-header-padding-bottom);
      background:       var(--ds-modal-bottom-sheet-background, #fff);
      flex-shrink:      0;
    }


    .ds-modal-bottom-sheet__header-column {
      display:          flex;
      align-items:      flex-start;
      gap:              var(--ds-sheet-header-gap, 8px);
      min-width:        var(--ds-sheet-header-side-min-width, 36px);
    }

    .ds-modal-bottom-sheet__header-center {
      min-width:        0;
      display:          flex;
      flex-direction:   column;
      align-items:      flex-start;
      justify-content:  center;
      gap:              var(--ds-modal-bottom-sheet-title-icon-gap);
      text-align:       center;
      width:            100%;
    }

    .ds-modal-bottom-sheet__header-center--has-close {
      box-sizing:       border-box;
      padding-left:     var(--ds-sheet-header-side-min-width, 36px);
    }

    .ds-modal-bottom-sheet__header-column--right {
      justify-content:  flex-end;
      min-width:        0;
    }

    .ds-modal-bottom-sheet__title-actions {
      display:          flex;
      align-items:      flex-start;
      justify-content:  center;
      gap:              var(--ds-sheet-header-gap, 8px);
      width:            100%;
    }

    .ds-modal-bottom-sheet__close-container {
      width:            var(--ds-sheet-header-side-min-width, 36px);
      height:           var(--ds-sheet-header-side-min-width, 36px);
      display:          flex;
      align-items:      flex-start;
      justify-content:  flex-end;
      flex:             0 0 var(--ds-sheet-header-side-min-width, 36px);
    }

    /* ── Body ────────────────────────────────────────────────── */
    .ds-modal-bottom-sheet__body {
      position:         relative;
      z-index:          1;
      display:          flex;
      flex-direction:   column;
      align-items:      center;
      gap:              var(--ds-modal-bottom-sheet-content-gap);
      width:            100%;
      padding:          var(--ds-modal-bottom-sheet-body-padding-top, 0px) var(--ds-modal-bottom-sheet-padding-x) var(--ds-sheet-body-padding-y, 8px);
      box-sizing:       border-box;
      align-self:       center;
      flex:             1 1 auto;
      min-height:       0;
      overflow-y:       auto;
    }

    .ds-modal-bottom-sheet__icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .ds-modal-bottom-sheet__text {
      display:          flex;
      flex-direction:   column;
      align-items:      center;
      gap:              var(--ds-modal-bottom-sheet-gap);
      width:            100%;
    }

    .ds-modal-bottom-sheet__title-icon {
      display:          flex;
      flex-direction:   column;
      align-items:      center;
      gap:              var(--ds-modal-bottom-sheet-title-icon-gap);
      width:            100%;
    }

    .ds-modal-bottom-sheet__title {
      margin:           0;
      color:            var(--ds-modal-bottom-sheet-title-color);
      text-align:       center;
    }

    .ds-modal-bottom-sheet__description {
      margin:           0;
      color:            var(--ds-modal-bottom-sheet-description-color);
      text-align:       center;
    }

    .ds-modal-bottom-sheet__footer {
      position: relative;
      z-index: 2;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--ds-modal-bottom-sheet-gap);
      padding: var(--ds-modal-bottom-sheet-padding-top) var(--ds-modal-bottom-sheet-padding-x) var(--ds-modal-bottom-sheet-padding-bottom);
      box-sizing: border-box;
      flex-shrink: 0;
    }

    .ds-modal-bottom-sheet__footer:empty {
      display: none;
    }

    .ds-modal-bottom-sheet__footer ::ng-deep [ds-modal-bottom-sheet-action] {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--ds-modal-bottom-sheet-gap);
    }

    .ds-modal-bottom-sheet__footer ::ng-deep ds-button-v2 .ds-btn {
      width: 100%;
      max-width: none;
    }

    @media (min-width: 840px) {
      .ds-modal-bottom-sheet {
        align-items: center;
      }

      .ds-modal-bottom-sheet__sheet {
        width:         calc(100% - (var(--ds-modal-bottom-sheet-viewport-margin) * 2));
        max-width:     var(--ds-modal-bottom-sheet-resolved-max-width, var(--ds-modal-bottom-sheet-default-max-width));
        max-height:    calc(var(--ds-modal-bottom-sheet-viewport-height, 100dvh) - var(--ds-modal-bottom-sheet-max-height-offset, 48px));
        border-radius: var(--ds-modal-bottom-sheet-border-radius);
        transform:     translateY(0);
      }
    }
  `],
})
export class DsModalBottomSheetComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly globalOverlayHost = inject(DsGlobalOverlayHostService);
  private viewportCleanup?: () => void;
  private overlayAttachment?: DsGlobalOverlayAttachment;
  private viewInitialized = false;

  @Input() visible = false;
  @Input() title = '';
  @Input() description = '';
  @Input() dismissOnBackdropClick = true;
  @Input({ transform: booleanAttribute }) showCloseButton = true;
  @Input({ transform: booleanAttribute }) showBackButton = false;
  @Input() showIcon = false;
  @Input() iconName: DsIconName | string = 'VisibilityFill';
  @Input() iconColor = 'var(--ds-modal-bottom-sheet-title-color)';
  @Input() set maxWidth(value: number | string) {
    this.resolvedMaxWidth = this.toCssLength(value, 'var(--ds-modal-bottom-sheet-default-max-width)');
  }

  @Output() dismissed = new EventEmitter<void>();
  @Output() backed = new EventEmitter<void>();

  _entering = false;
  _viewportHeight: number | null = null;
  _keyboardOffset: number | null = null;
  resolvedMaxWidth = 'var(--ds-modal-bottom-sheet-default-max-width)';

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    if (this.visible) {
      this.attachToGlobalOverlay();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      if (this.viewInitialized) {
        this.attachToGlobalOverlay();
      }
      this._entering = true;
      requestAnimationFrame(() => { this._entering = false; });
      this.startViewportTracking();
    }

    if (changes['visible']?.currentValue === false) {
      this.stopViewportTracking();
    }
  }

  ngOnDestroy(): void {
    this.stopViewportTracking();
    this.overlayAttachment?.detach();
    this.overlayAttachment = undefined;
  }

  onOverlayClick(event: MouseEvent): void {
    if (this.dismissOnBackdropClick && event.target === event.currentTarget) {
      this.dismissed.emit();
    }
  }

  private toCssLength(value: number | string | null | undefined, fallback: string): string {
    if (typeof value === 'number') return `${value}px`;
    if (typeof value === 'string' && value.trim().length > 0) {
      const trimmed = value.trim();
      return /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
    }
    return fallback;
  }

  private startViewportTracking(): void {
    this.stopViewportTracking();

    if (typeof window === 'undefined') return;

    const visualViewport = window.visualViewport;
    const updateViewport = () => {
      if (!this.visible) return;

      const viewportHeight = visualViewport?.height ?? window.innerHeight;
      const viewportOffsetTop = visualViewport?.offsetTop ?? 0;
      const keyboardOffset = Math.max(0, window.innerHeight - viewportHeight - viewportOffsetTop);

      this._viewportHeight = viewportHeight;
      this._keyboardOffset = keyboardOffset;
      this.cdr.markForCheck();
    };

    updateViewport();
    window.addEventListener('resize', updateViewport, { passive: true });
    visualViewport?.addEventListener('resize', updateViewport, { passive: true });
    visualViewport?.addEventListener('scroll', updateViewport, { passive: true });

    this.viewportCleanup = () => {
      window.removeEventListener('resize', updateViewport);
      visualViewport?.removeEventListener('resize', updateViewport);
      visualViewport?.removeEventListener('scroll', updateViewport);
    };
  }

  private stopViewportTracking(): void {
    this.viewportCleanup?.();
    this.viewportCleanup = undefined;
    this._viewportHeight = null;
    this._keyboardOffset = null;
  }

  private attachToGlobalOverlay(): void {
    if (this.overlayAttachment) {
      this.overlayAttachment.bringToFront();
      return;
    }

    this.overlayAttachment = this.globalOverlayHost.attach(this.host.nativeElement) ?? undefined;
  }
}
